// Ported from dev-scrips-pm-react src/scheduling/components/view-appointment-panel/billing-tab.tsx (PROD-3312).
import { useEffect, useState } from 'react';
import { AlertTriangle, Receipt } from 'lucide-react';
import { InvoiceDetailPage } from '@/billing/pages/invoice-detail-page';
import { OpenCashierShiftDialog } from '@/billing/components/open-cashier-shift-dialog';
import { usePostBookCalculatePrice } from '@/services/scheduling/use-postbook-calculate-price';
import type { PreLoadAppointmentResponse } from '@/types/scheduling-api';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { EMPTY_GUID } from '@/shared/lib/empty-guid';

/**
 * ViewAppointmentBillingTab — appointment-panel Billing tab body, a 1:1 mirror
 * of pm-react's (PROD-798 there). Mounts `InvoiceDetailPage` in embedded mode
 * instead of rebuilding the invoice surface.
 *
 * Flutter parity: `tab_widget/billing_tab.dart`. The Flutter widget
 * fires two requests on mount:
 *   1. PreLoadAppointmentDetails — already done upstream by the panel page
 *      (`useAppointmentDetails`); the invoice id we need lives at
 *      `data.billingData.invoice.id`.
 *   2. PostBookCalculatePrice — re-calculates the pricing breakdown
 *      using the appointment's current sponsor type. We fire-and-forget
 *      via `usePostBookCalculatePrice`; the recalc happens server-side
 *      on the invoice record and the next `useInvoice` refetch picks
 *      up the new totals (pm-react "Plan A").
 *
 * Cashier-shift guard: the invoice body is passed `onCashierShiftRequired`
 * so a `"Cashier's shift is not open"` error pops `OpenCashierShiftDialog`
 * instead of leaving the user with just the global error toast. In pm-react
 * the dialog's open state is held LOCAL to this component (never hoisted to
 * the panel page).
 *
 * PostBookCalculatePrice failure handling: on tab mount the recalc
 * fires fire-and-forget; if it rejects, we surface a non-blocking
 * inline warning banner so a clinician quoting figures knows the
 * displayed totals may be stale.
 *
 * Visibility gate (handled in `view-appointment-panel-page.tsx`):
 *   `Show_Billing_Tab` permission + appointment status not in early
 *   state, or (device booking) a persisted invoice id.
 */

interface ViewAppointmentBillingTabProps {
  data: PreLoadAppointmentResponse | null;
}

export function ViewAppointmentBillingTab({ data }: ViewAppointmentBillingTabProps) {
  const invoiceId = data?.billingData?.invoice?.id ?? undefined;
  const patientId = findPatientParticipant(data?.appointment?.participant)?.patient?.id;
  const providerId = data?.appointment?.participant?.find((p) => {
    const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
    return t === 'practitioner' || t === 'provider';
  })?.provider?.id;
  const rawSponsorType = data?.appointment?.sponsorType;
  const sponsorId =
    (data?.appointment?.sponsor as { id?: string } | undefined)?.id ?? undefined;

  // Local state, never hoisted, so closing the cashier-shift dialog doesn't
  // affect any other panel surface (pm-react, Samer review PR #129 §5).
  const [cashierShiftOpen, setCashierShiftOpen] = useState(false);
  const postBookCalc = usePostBookCalculatePrice();

  // PostBookCalculatePrice (Flutter parity: billing_tab.dart fires this on
  // mount). Synchronises the server-side invoice pricing with the appointment's
  // current sponsor. Fire-and-forget — the page reads pricing off `useInvoice`.
  //
  // NOT gated on `providerId` (pm-react PROD-2007): a device booking has no
  // practitioner; the aggregator drops the provider key, and Guid.Empty is what
  // Billing stores on a device invoice.
  useEffect(() => {
    if (!invoiceId || !patientId) return;
    const paymentType = normaliseSponsorType(rawSponsorType);
    postBookCalc.mutate(
      {
        patientId,
        invoiceId,
        providerId: providerId || EMPTY_GUID,
        paymentType,
        // The hook strips `sponsorId` from the body for Self-pay
        // (see use-postbook-calculate-price.ts).
        ...(paymentType !== 'Self-pay' && sponsorId ? { sponsorId } : {}),
        individualServices: [],
      },
      {
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error('[PostBookCalculatePrice]', err);
        },
      },
    );
    // We intentionally don't depend on `postBookCalc` — the mutation object is
    // a new reference each render and would re-trigger the effect endlessly.
    // Fire once as soon as the ids land, again if the sponsor changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, patientId, providerId, rawSponsorType, sponsorId]);

  if (!invoiceId) {
    return <NoInvoiceState />;
  }

  return (
    <>
      {/* Non-blocking inline warning when the on-mount recalc failed. The
          displayed totals are whatever was last persisted on the invoice
          record; they may not reflect the current sponsor type. */}
      {postBookCalc.isError && (
        <div className="mx-8 mt-4 flex items-start gap-2 rounded-lg border border-yellow-100 bg-yellow-100 px-3 py-2 text-sm text-yellow-700">
          <AlertTriangle className="w-4 h-4 mt-1 shrink-0" aria-hidden />
          <div>
            <div className="font-semibold">Pricing may be out of date</div>
            <div className="mt-1">
              The on-open price recalculation didn't complete. The totals
              shown below reflect the last persisted invoice; verify
              before quoting figures to the patient.
            </div>
          </div>
        </div>
      )}
      <InvoiceDetailPage
        invoiceId={invoiceId}
        embedded
        onCashierShiftRequired={() => setCashierShiftOpen(true)}
        availableBalance={data?.billingData?.availableBalance?.balance}
      />
      <OpenCashierShiftDialog open={cashierShiftOpen} onOpenChange={setCashierShiftOpen} />
    </>
  );
}

/**
 * Coerce the appointment's free-text `sponsorType` to the union
 * `PostBookCalculatePriceRequest.paymentType` accepts. Anything we
 * don't recognise (legacy values, blank) falls back to Self-pay —
 * matches Flutter's default branch in payment_widget.dart.
 */
function normaliseSponsorType(
  raw: string | undefined,
): 'Self-pay' | 'Insurance' | 'Corporate' {
  const t = (raw ?? '').toLowerCase();
  if (t === 'insurance') return 'Insurance';
  if (t === 'corporate' || t === 'corporates') return 'Corporate';
  return 'Self-pay';
}

function NoInvoiceState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-8 py-12">
      <Receipt className="w-8 h-8 text-text-muted" aria-hidden />
      <div className="text-sm text-text-primary font-semibold">
        No invoice yet
      </div>
      <p className="text-sm text-text-muted max-w-md text-center">
        This appointment doesn't have an invoice associated with it.
        Once the appointment is checked in or its profile triggers an
        invoice draft, the billing surface will load here.
      </p>
    </div>
  );
}
